import { useCallback, useEffect, useState } from "react"
import { ButtonPrimary, ButtonTertiary, IconButton } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { useRouter } from "pages"
import { useCreateCurrencyPolicy } from "features/pilot-tracking/currency/CurrencyPolicyQueries"
import { getGenericWorkspacePage, getGenericWorkspacePageHeader } from "../WorkspaceRoutes"
import { CurrencyPolicyForm } from "./CurrencyPolicyForm"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import {
    resetForm,
    selectFormAsCreatable,
    selectSubmitting,
    setSubmitting,
    updateForm,
} from "features/pilot-tracking/currency/slice"
import { createQuery } from "@nova/queryable"
import { usePilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"

const policyCountQuery = createQuery({
    resource: "currency_policies",
    select: ["id"],
})

function BackButton() {
    const router = useRouter("/workspace/:workspaceId")
    return (
        <IconButton
            onClick={() => router.navigate({ path: "/fleet/pilots/currency" })}
            className="pr-2"
        >
            <Icons.SmallIcon Icon={Icons.ArrowLeft} />
        </IconButton>
    )
}

function CancelButton() {
    const router = useRouter("/workspace/:workspaceId")
    const submitting = useAppSelector(selectSubmitting)
    return (
        <ButtonTertiary
            title="Cancel"
            onClick={() => router.navigate({ path: "/fleet/pilots/currency" })}
            disabled={submitting}
        />
    )
}

function PublishPolicyButton() {
    const form = useAppSelector(selectFormAsCreatable)
    const { createCurrencyPolicy } = useCreateCurrencyPolicy()
    const dispatch = useAppDispatch()
    const router = useRouter("/workspace/:workspaceId")
    const submitting = useAppSelector(selectSubmitting)

    const handleSubmit = useCallback(async () => {
        dispatch(setSubmitting(true))
        try {
            await createCurrencyPolicy(form)
            dispatch(
                setGlobalNotificationContext({
                    message: `${form.name} has been published.`,
                    status: GlobalNotificationStatus.success,
                    timeoutInMs: 5000,
                })
            )
            router.navigate({ path: "/fleet/pilots/currency" })
        } catch {
            dispatch(
                setGlobalNotificationContext({
                    message: "Failed to publish the policy. Please try again.",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 5000,
                })
            )
        } finally {
            dispatch(setSubmitting(false))
        }
    }, [form, createCurrencyPolicy, dispatch, router])

    return (
        <ButtonPrimary
            title="Publish"
            RightIcon={Icons.Checkmark}
            onClick={handleSubmit}
            disabled={submitting}
        />
    )
}

export const CreateCurrencyPolicyPage = getGenericWorkspacePage({
    Header: getGenericWorkspacePageHeader({
        title: "Create Policy",
        leftItems: [BackButton],
        rightItems: [CancelButton, PublishPolicyButton],
        border: true,
    }),
    Content: CreateCurrencyPolicyPageContent,
    maxWidth: 1200,
})

function CreateCurrencyPolicyPageContent() {
    const dispatch = useAppDispatch()
    const [isFormReady, setIsFormReady] = useState(false)
    const { data: policies, isLoading } = usePilotTrackingQuery(policyCountQuery)

    useEffect(() => {
        if (!isLoading && policies && !isFormReady) {
            dispatch(resetForm())
            dispatch(updateForm({ key: "name", value: `Policy #${policies.length + 1}` }))
            setIsFormReady(true)
        }
    }, [isLoading, policies, isFormReady, dispatch])

    if (!isFormReady) {
        return (
            <div className="w-full h-full flex justify-center items-center">
                <div className="loader" />
            </div>
        )
    }

    return <CurrencyPolicyForm />
}
